import { NEWS } from '@utils/constants';
import { NewsItem, NewsResponse } from '../types/news';

const CACHE_KEY = 'plick_blog_news_cache';
// The blog publishes ~monthly and the n8n webhook is backed by its own Redis
// cache (invalidated by Ghost's post.published webhook), so the origin is
// already fresh. This client-side cache only throttles how often each browser
// hits the webhook; keep it short so a freshly published article surfaces soon
// without polling on a blind interval.
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Single-flight guard: the mount effect plus the focus/visibility listeners can
// all ask for news within the same instant. Sharing one in-flight promise means
// a burst of triggers results in at most one network request.
let inFlight: Promise<NewsItem[]> | null = null;

const normalizeItems = (items: NewsItem[]): NewsItem[] => {
  const cleanedItems = items.map((item) => {
    let cleanCategory;
    if (typeof item.category === 'string') {
      try {
        // Intentamos parsear si es un string que contiene un array JSON
        cleanCategory = JSON.parse(item.category);
      } catch {
        // Si falla el parse, usamos el string tal cual
        cleanCategory = item.category;
      }
    } else {
      cleanCategory = item.category;
    }

    // Aseguramos que sea array, descartamos valores no-string (null incluido) y limpiamos cada elemento
    const categoryArray = Array.isArray(cleanCategory) ? cleanCategory : [cleanCategory];
    const finalCategory = categoryArray
      .filter((cat): cat is string => typeof cat === 'string')
      .map((cat) => cat.trim());

    return {
      ...item,
      category: finalCategory,
    };
  });

  return cleanedItems.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

const requestNews = async (): Promise<NewsItem[]> => {
  try {
    // Check cache first
    if (typeof window !== 'undefined') {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { timestamp, data } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) {
            return data;
          }
        } catch {
          // If parsing fails, proceed to fetch
        }
      }
    }

    const response = await fetch(NEWS.API_URL);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data: NewsResponse = await response.json();

    const sortedItems = normalizeItems(data.items);

    // Update cache
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            timestamp: Date.now(),
            data: sortedItems,
          }),
        );
      } catch {
        // Ignore storage errors
      }
    }

    return sortedItems;
  } catch {
    // El blog es contenido no crítico servido por una API externa. No reportamos
    // sus fallos a Sentry (decisión de costo/ruido): degradamos en silencio
    // devolviendo una lista vacía — el feed simplemente no muestra noticias.
    return [];
  }
};

export const fetchNews = (): Promise<NewsItem[]> => {
  if (inFlight) {
    return inFlight;
  }
  inFlight = requestNews().finally(() => {
    inFlight = null;
  });
  return inFlight;
};
